import { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Play, Pause, Volume2, VolumeX, ShoppingCart, Zap } from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { useCart } from "../App";
import WaveSurfer from "wavesurfer.js";
import LikeButton from "./LikeButton";

const BeatPlayer = ({ beat, showDetails = false }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isReady, setIsReady] = useState(false);
  const waveformRef = useRef(null);
  const wavesurferRef = useRef(null);
  const isMountedRef = useRef(true);
  const { addToCart } = useCart();

  // SoundCloud detection
  const isSoundCloud = beat.soundcloud_url || (beat.preview_url && beat.preview_url.includes("soundcloud.com"));
  const hasDirectAudio = beat.audio_url && !beat.audio_url.includes("soundcloud.com");
  const soundcloudEmbedUrl = isSoundCloud
    ? `https://w.soundcloud.com/player/?url=${encodeURIComponent(beat.soundcloud_url || beat.preview_url)}&color=%2300f3ff&auto_play=false&hide_related=true&show_comments=false&show_user=false&show_reposts=false&show_teaser=false`
    : null;

  useEffect(() => {
    isMountedRef.current = true;
    
    const initWaveSurfer = async () => {
      // Skip WaveSurfer for SoundCloud-only beats without direct audio
      if (!waveformRef.current || (!beat.audio_url && isSoundCloud)) {
        if (isMountedRef.current) setIsLoading(false);
        return;
      }
      if (!beat.audio_url && !beat.preview_url) {
        if (isMountedRef.current) setIsLoading(false);
        return;
      }
      
      // Clean up any existing instance first
      if (wavesurferRef.current) {
        try {
          wavesurferRef.current.destroy();
        } catch (e) {
          // Ignore destroy errors
        }
        wavesurferRef.current = null;
      }

      try {
        const ws = WaveSurfer.create({
          container: waveformRef.current,
          waveColor: '#3f3f46',
          progressColor: '#00f3ff',
          cursorColor: '#ff00ff',
          barWidth: 2,
          barGap: 2,
          barRadius: 0,
          height: 48,
          normalize: true,
        });

        wavesurferRef.current = ws;

        ws.on('ready', () => {
          if (isMountedRef.current) {
            setDuration(ws.getDuration());
            setIsLoading(false);
            setIsReady(true);
          }
        });

        ws.on('audioprocess', () => {
          if (isMountedRef.current && ws) {
            setCurrentTime(ws.getCurrentTime());
          }
        });

        ws.on('play', () => {
          if (isMountedRef.current) setIsPlaying(true);
        });
        
        ws.on('pause', () => {
          if (isMountedRef.current) setIsPlaying(false);
        });
        
        ws.on('finish', () => {
          if (isMountedRef.current) setIsPlaying(false);
        });

        ws.on('error', (err) => {
          console.warn('WaveSurfer error:', err);
          if (isMountedRef.current) {
            setIsLoading(false);
          }
        });

        // Load audio
        await ws.load(beat.preview_url || beat.audio_url);
      } catch (error) {
        console.warn('WaveSurfer init error:', error);
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      }
    };

    initWaveSurfer();

    return () => {
      isMountedRef.current = false;
      if (wavesurferRef.current) {
        try {
          wavesurferRef.current.pause();
          wavesurferRef.current.destroy();
        } catch (e) {
          // Ignore cleanup errors
        }
        wavesurferRef.current = null;
      }
    };
  }, [beat.beat_id]); // Only re-init when beat ID changes

  const togglePlay = useCallback(() => {
    if (wavesurferRef.current && isReady) {
      wavesurferRef.current.playPause();
    }
  }, [isReady]);

  const toggleMute = useCallback(() => {
    if (wavesurferRef.current && isReady) {
      const newMuted = !isMuted;
      wavesurferRef.current.setMuted(newMuted);
      setIsMuted(newMuted);
    }
  }, [isMuted, isReady]);

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleQuickAdd = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    await addToCart({
      item_type: "beat",
      item_id: beat.beat_id,
      license_type: "basic",
      quantity: 1
    });
  };

  return (
    <div 
      className="group glass p-4 md:p-6 card-hover"
      data-testid={`beat-player-${beat.beat_id}`}
    >
      <div className="flex items-center gap-4">
        {/* Play Button */}
        {isSoundCloud && !hasDirectAudio ? (
          <a
            href={beat.soundcloud_url || beat.preview_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 w-14 h-14 bg-orange-500/20 hover:bg-orange-500/30 flex items-center justify-center transition-colors"
            data-testid={`play-button-${beat.beat_id}`}
          >
            <Play className="w-6 h-6 text-orange-400" fill="currentColor" />
          </a>
        ) : (
          <button
            onClick={togglePlay}
            disabled={isLoading}
            className="flex-shrink-0 w-14 h-14 bg-neon-cyan/20 hover:bg-neon-cyan/30 flex items-center justify-center transition-colors disabled:opacity-50"
            data-testid={`play-button-${beat.beat_id}`}
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-neon-cyan border-t-transparent rounded-full animate-spin" />
            ) : isPlaying ? (
              <Pause className="w-6 h-6 text-neon-cyan" fill="currentColor" />
            ) : (
              <Play className="w-6 h-6 text-neon-cyan" fill="currentColor" />
            )}
          </button>
        )}

        {/* Beat Image (Mobile Hidden) */}
        <Link to={`/beats/${beat.beat_id}`} className="hidden md:block flex-shrink-0">
          <div className="w-14 h-14 bg-zinc-800 overflow-hidden">
            <img
              src={beat.image_url || "https://images.unsplash.com/photo-1741746239350-9021ddfa3d59?w=100"}
              alt={beat.title}
              className="w-full h-full object-cover"
            />
          </div>
        </Link>

        {/* Info & Waveform */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-2">
            <Link to={`/beats/${beat.beat_id}`} className="hover:text-neon-cyan transition-colors">
              <h3 className="font-medium truncate">{beat.title}</h3>
            </Link>
            <div className="hidden sm:flex items-center gap-2">
              <Badge variant="outline" className="text-xs border-white/20">
                {beat.bpm} BPM
              </Badge>
              <Badge variant="outline" className="text-xs border-white/20">
                {beat.key}
              </Badge>
              <Badge className="bg-neon-cyan/20 text-neon-cyan border-neon-cyan/50 text-xs">
                {beat.genre}
              </Badge>
            </div>
          </div>

          {/* Waveform or SoundCloud Player */}
          <div className="relative">
            {isSoundCloud && !hasDirectAudio ? (
              <iframe
                title="SoundCloud"
                width="100%"
                height="48"
                scrolling="no"
                frameBorder="no"
                allow="autoplay"
                src={soundcloudEmbedUrl}
                style={{ background: 'transparent' }}
              />
            ) : (
              <>
                <div 
                  ref={waveformRef} 
                  className="waveform-container cursor-pointer"
                />
                {isLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-zinc-900/80">
                    <div className="w-full h-1 bg-zinc-800 rounded">
                      <div className="h-full bg-neon-cyan/50 rounded animate-pulse" style={{ width: '60%' }} />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Time & Controls */}
          <div className="flex items-center justify-between mt-2 text-xs text-zinc-500">
            <span className="font-mono">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
            <div className="flex items-center gap-2">
              <span className="font-mono text-zinc-600">{beat.plays?.toLocaleString()} plays</span>
              <button onClick={toggleMute} className="p-1 hover:text-neon-cyan transition-colors">
                {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>

        {/* Price & Add to Cart */}
        <div className="flex-shrink-0 text-right hidden sm:flex sm:flex-col sm:items-end sm:gap-1">
          <LikeButton itemType="beat" itemId={beat.beat_id} size="sm" />
          {beat.on_sale ? (
            <>
              <div className="flex items-center gap-1">
                <Zap className="w-3 h-3 text-red-400" />
                <Badge className="bg-red-500/20 text-red-400 border-red-500/50 text-[10px]">{beat.sale_discount}% OFF</Badge>
              </div>
              <p className="text-red-400 font-mono text-lg font-bold">
                ${beat.basic_price?.toFixed(2)}
              </p>
              <p className="text-zinc-500 font-mono text-xs line-through">
                ${beat.original_basic_price?.toFixed(2)}
              </p>
            </>
          ) : (
            <>
              <p className="text-neon-cyan font-mono text-lg font-bold">
                ${beat.basic_price?.toFixed(2)}
              </p>
              <span className="text-xs text-zinc-500 uppercase">Basic License</span>
            </>
          )}
          <Button
            onClick={handleQuickAdd}
            size="sm"
            className="mt-2 bg-neon-cyan/20 text-neon-cyan hover:bg-neon-cyan hover:text-black text-xs"
            data-testid={`add-to-cart-${beat.beat_id}`}
          >
            <ShoppingCart className="w-3 h-3 mr-1" />
            Add
          </Button>
          <Link to={`/customizer/${beat.beat_id}`} className="text-[10px] text-zinc-500 hover:text-neon-magenta transition mt-1" data-testid={`customize-${beat.beat_id}`}>
            Customize Mix
          </Link>
        </div>
      </div>

      {/* Mobile Price Row */}
      <div className="sm:hidden flex items-center justify-between mt-4 pt-4 border-t border-white/10">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs border-white/20">
            {beat.bpm} BPM
          </Badge>
          <Badge className="bg-neon-cyan/20 text-neon-cyan text-xs">
            {beat.genre}
          </Badge>
        </div>
        <div className="flex items-center gap-3">
          <LikeButton itemType="beat" itemId={beat.beat_id} size="sm" />
          {beat.on_sale ? (
            <div className="flex items-center gap-2">
              <span className="text-red-400 font-mono font-bold">${beat.basic_price?.toFixed(2)}</span>
              <span className="text-zinc-600 font-mono text-xs line-through">${beat.original_basic_price?.toFixed(2)}</span>
            </div>
          ) : (
            <span className="text-neon-cyan font-mono font-bold">${beat.basic_price?.toFixed(2)}</span>
          )}
          <Button
            onClick={handleQuickAdd}
            size="sm"
            className="bg-neon-cyan text-black"
            data-testid={`mobile-add-to-cart-${beat.beat_id}`}
          >
            <ShoppingCart className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BeatPlayer;
